import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Users, UserPlus, Check, X, ChevronDown, Snowflake, ShieldCheck, Trash2, Clock, ArrowUpRight, ArrowDownLeft, PiggyBank, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { api, errMsg } from "../lib/api";
import { Spinner, AuthedAvatar, EmptyState } from "../components/common";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-primary" : "bg-muted"}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

function ControlRow({ title, desc, children }) {
  return (
    <div className="flex items-center gap-3 py-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">{title}</div>
        {desc && <div className="text-[12px] text-muted-foreground leading-snug">{desc}</div>}
      </div>
      {children}
    </div>
  );
}

function AddTeenModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ full_name: "", email: "", password: "", dob: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.password || !form.dob) { toast.error("Fill in every field"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/family/teens", form);
      toast.success(`${data.display_name}'s account created`);
      onCreated(data);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-6" onClick={onClose}>
      <div data-testid="add-teen-modal" onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-card border border-border rounded-t-3xl sm:rounded-3xl p-6 animate-fade-up">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-xl font-semibold">Add a teen</h2>
          <button data-testid="add-teen-close" onClick={onClose} className="grid place-items-center w-9 h-9 rounded-full bg-secondary text-muted-foreground"><X size={18} /></button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Ages 13–17. They'll sign in to Jack Cash with their own email and password.</p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Full name</label>
            <input data-testid="teen-name-input" value={form.full_name} onChange={set("full_name")} placeholder="Teen's full name"
              className="w-full h-12 px-3 mt-1 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Email</label>
            <input data-testid="teen-email-input" type="email" value={form.email} onChange={set("email")} placeholder="teen@email.com"
              className="w-full h-12 px-3 mt-1 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Password</label>
            <input data-testid="teen-password-input" type="password" value={form.password} onChange={set("password")} placeholder="Min 8 chars incl. @ # $ ! % &"
              className="w-full h-12 px-3 mt-1 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Date of birth</label>
            <input data-testid="teen-dob-input" type="date" value={form.dob} onChange={set("dob")}
              className="w-full h-12 px-3 mt-1 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <button type="submit" disabled={busy} data-testid="teen-create-btn"
            className="w-full h-13 py-3.5 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Spinner className="w-5 h-5" /> : <><UserPlus size={18} /> Create account</>}
          </button>
        </form>
      </div>
    </div>
  );
}

function TeenCard({ teen, onChanged }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [insights, setInsights] = useState(null);
  const [c, setC] = useState(teen.controls);
  const [al, setAl] = useState(teen.allowance);
  const [busy, setBusy] = useState(false);
  const [alBusy, setAlBusy] = useState(false);

  useEffect(() => { setC(teen.controls); }, [teen.controls]);
  useEffect(() => { setAl(teen.allowance); }, [teen.allowance]);

  const expand = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) {
      try {
        const [d, ins] = await Promise.all([
          api.get(`/family/teens/${teen.teen_id}`),
          api.get(`/family/teens/${teen.teen_id}/insights`),
        ]);
        setDetail(d.data); setInsights(ins.data);
      } catch (err) { toast.error(errMsg(err)); }
    }
  };

  const saveAllowance = async () => {
    if (al.enabled && (!al.amount_dollars || al.amount_dollars <= 0)) { toast.error("Set a weekly amount"); return; }
    if (al.enabled && al.day_of_week === null) { toast.error("Pick a day"); return; }
    setAlBusy(true);
    try {
      const { data } = await api.put(`/family/teens/${teen.teen_id}/allowance`, {
        enabled: al.enabled, amount: al.amount_dollars || 0,
        day_of_week: al.day_of_week,
      });
      setAl(data.allowance);
      toast.success(data.allowance.enabled ? "Allowance set" : "Allowance turned off");
      onChanged();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setAlBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/family/teens/${teen.teen_id}/controls`, {
        can_send: c.can_send, require_approval: c.require_approval, frozen: c.frozen,
        daily_limit: c.daily_limit_dollars || 0, weekly_limit: c.weekly_limit_dollars || 0,
      });
      toast.success("Settings saved");
      setC(data.controls);
      onChanged();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const removeTeen = async () => {
    if (!window.confirm(`Remove ${teen.display_name} from Family? Their account stays but becomes self-managed.`)) return;
    try { await api.delete(`/family/teens/${teen.teen_id}`); toast.success("Removed"); onChanged(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid={`teen-card-${teen.teen_id}`} className="rounded-3xl bg-card border border-border overflow-hidden">
      <button onClick={expand} data-testid={`teen-expand-${teen.teen_id}`} className="w-full flex items-center gap-3 p-4 text-left hover:bg-secondary/40 transition-colors">
        <AuthedAvatar src={teen.profile_photo} name={teen.display_name} size={48} className="rounded-full" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold truncate">{teen.display_name}</span>
            {teen.frozen && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-sky-600 bg-sky-500/10 px-2 py-0.5 rounded-full"><Snowflake size={11} /> FROZEN</span>}
          </div>
          <div className="text-xs text-muted-foreground font-mono truncate">{teen.cash_tag}</div>
        </div>
        <div className="text-right">
          <div className="amount-figure text-lg">{teen.balance_display}</div>
          {teen.pending_approvals > 0 && <div className="text-[11px] font-bold text-warning">{teen.pending_approvals} to approve</div>}
        </div>
        <ChevronDown size={18} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-border">
          <div className="divide-y divide-border">
            <ControlRow title="Allow sending" desc="If off, they can only receive money">
              <Toggle checked={c.can_send} onChange={(v) => setC({ ...c, can_send: v })} testid={`teen-cansend-${teen.teen_id}`} />
            </ControlRow>
            <ControlRow title="Approve every payment" desc="Each send waits for your approval">
              <Toggle checked={c.require_approval} onChange={(v) => setC({ ...c, require_approval: v })} testid={`teen-approval-${teen.teen_id}`} />
            </ControlRow>
            <ControlRow title="Freeze account" desc="Blocks all sending immediately">
              <Toggle checked={c.frozen} onChange={(v) => setC({ ...c, frozen: v })} testid={`teen-freeze-${teen.teen_id}`} />
            </ControlRow>
            <div className="py-3 grid grid-cols-2 gap-3">
              <div>
                <label className="text-[12px] font-semibold text-muted-foreground">Daily limit ($)</label>
                <input type="number" min="0" step="1" data-testid={`teen-daily-${teen.teen_id}`}
                  value={c.daily_limit_dollars ?? ""} onChange={(e) => setC({ ...c, daily_limit_dollars: e.target.value === "" ? null : parseFloat(e.target.value) })}
                  placeholder="No limit" className="w-full h-11 px-3 mt-1 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-muted-foreground">Weekly limit ($)</label>
                <input type="number" min="0" step="1" data-testid={`teen-weekly-${teen.teen_id}`}
                  value={c.weekly_limit_dollars ?? ""} onChange={(e) => setC({ ...c, weekly_limit_dollars: e.target.value === "" ? null : parseFloat(e.target.value) })}
                  placeholder="No limit" className="w-full h-11 px-3 mt-1 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
            </div>
          </div>

          <button onClick={save} disabled={busy} data-testid={`teen-save-${teen.teen_id}`}
            className="w-full h-12 mt-2 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Spinner className="w-5 h-5" /> : <><Check size={18} /> Save settings</>}
          </button>

          {/* Weekly allowance */}
          <div className="mt-5 rounded-2xl bg-secondary/50 border border-border p-4" data-testid={`teen-allowance-${teen.teen_id}`}>
            <div className="flex items-center gap-2 mb-3">
              <PiggyBank size={17} className="text-primary" />
              <span className="font-semibold text-sm flex-1">Weekly allowance</span>
              <Toggle checked={!!al?.enabled} onChange={(v) => setAl({ ...al, enabled: v })} testid={`teen-allowance-toggle-${teen.teen_id}`} />
            </div>
            {al?.enabled && (
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-[12px] font-semibold text-muted-foreground">Amount ($)</label>
                  <input type="number" min="1" step="1" data-testid={`teen-allowance-amount-${teen.teen_id}`}
                    value={al.amount_dollars ?? ""} onChange={(e) => setAl({ ...al, amount_dollars: e.target.value === "" ? null : parseFloat(e.target.value) })}
                    placeholder="10" className="w-full h-11 px-3 mt-1 rounded-xl bg-card outline-none focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-muted-foreground">Every</label>
                  <select data-testid={`teen-allowance-day-${teen.teen_id}`}
                    value={al.day_of_week ?? ""} onChange={(e) => setAl({ ...al, day_of_week: e.target.value === "" ? null : parseInt(e.target.value) })}
                    className="w-full h-11 px-3 mt-1 rounded-xl bg-card outline-none focus:ring-2 focus:ring-primary/30">
                    <option value="">Pick a day</option>
                    {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </select>
                </div>
              </div>
            )}
            <p className="text-[12px] text-muted-foreground mb-3">
              {al?.enabled
                ? `We'll send ${teen.display_name} ${al.amount_dollars ? "$" + al.amount_dollars : "this amount"} from your balance every ${al.day_of_week != null ? WEEKDAYS[al.day_of_week] : "chosen day"}.`
                : "Automatically send pocket money to this teen every week."}
            </p>
            <button onClick={saveAllowance} disabled={alBusy} data-testid={`teen-allowance-save-${teen.teen_id}`}
              className="w-full h-11 rounded-2xl bg-primary/10 text-primary font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {alBusy ? <Spinner className="w-4 h-4" /> : "Save allowance"}
            </button>
          </div>

          {/* Spending chart */}
          <div className="mt-5" data-testid={`teen-insights-${teen.teen_id}`}>
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 size={15} className="text-primary" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex-1">Last 7 days</span>
              {insights && <span className="text-[11px] text-muted-foreground">Spent <strong className="text-foreground">{insights.total_spent_display}</strong></span>}
            </div>
            {!insights ? <div className="py-6 grid place-items-center"><Spinner className="w-5 h-5 text-primary" /></div>
              : insights.total_spent === 0 && insights.total_received === 0 ? (
                <p className="text-sm text-muted-foreground py-3 text-center">No activity in the last 7 days.</p>
              ) : (
                <div className="h-36 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={insights.days} margin={{ top: 6, right: 4, left: 4, bottom: 0 }}>
                      <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                      <Tooltip cursor={{ fill: "rgba(75,35,204,0.06)" }}
                        contentStyle={{ borderRadius: 12, border: "1px solid var(--border)", fontSize: 12 }}
                        formatter={(v, n) => [`$${v}`, n === "spent_dollars" ? "Spent" : "Received"]} />
                      <Bar dataKey="spent_dollars" radius={[6, 6, 0, 0]} maxBarSize={26}>
                        {insights.days.map((d, i) => <Cell key={i} fill="#4B23CC" />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
          </div>

          <div className="mt-5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Recent activity</div>
            {!detail ? <div className="py-4 grid place-items-center"><Spinner className="w-5 h-5 text-primary" /></div>
              : detail.transactions?.length ? (
                <div className="divide-y divide-border">
                  {detail.transactions.slice(0, 8).map((t) => (
                    <div key={t.transaction_id} className="flex items-center gap-3 py-2.5">
                      <span className={`grid place-items-center w-8 h-8 rounded-full ${t.amount >= 0 ? "bg-success/10 text-success" : "bg-secondary text-primary"}`}>
                        {t.amount >= 0 ? <ArrowDownLeft size={15} /> : <ArrowUpRight size={15} />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{t.counterparty_name || t.description}</div>
                        <div className="text-[11px] text-muted-foreground">{new Date(t.timestamp).toLocaleDateString()}</div>
                      </div>
                      <div className={`text-sm font-semibold ${t.amount >= 0 ? "text-success" : ""}`}>{t.signed_display}</div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground py-2">No activity yet.</p>}
          </div>

          <button onClick={removeTeen} data-testid={`teen-remove-${teen.teen_id}`}
            className="w-full h-11 mt-4 rounded-2xl bg-destructive/10 text-destructive font-semibold flex items-center justify-center gap-2 text-sm">
            <Trash2 size={16} /> Remove from Family
          </button>
        </div>
      )}
    </div>
  );
}

export default function Family() {
  const [teens, setTeens] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, a] = await Promise.all([api.get("/family/teens"), api.get("/family/approvals")]);
      setTeens(t.data); setApprovals(a.data);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, kind) => {
    setActing(id + kind);
    try {
      const key = `idem_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      await api.post(`/family/approvals/${id}/${kind}`, {}, { headers: { "Idempotency-Key": key } });
      toast.success(kind === "approve" ? "Payment approved" : "Payment declined");
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setActing(null); }
  };

  return (
    <div className="animate-fade-up pb-4">
      <div className="relative overflow-hidden rounded-3xl p-6 text-white shadow-[0_18px_44px_rgba(75,35,204,0.4)] mb-6"
        style={{ background: "linear-gradient(135deg,#6B3BF5 0%,#4B23CC 55%,#3A1A9F 100%)" }}>
        <div className="absolute -right-8 -top-8 w-36 h-36 rounded-full bg-white/10" />
        <div className="absolute right-12 top-20 w-24 h-24 rounded-full bg-white/5" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1 text-[11px] font-bold tracking-wide uppercase mb-3">
            <Users size={13} /> Family
          </div>
          <h1 className="font-display text-2xl font-semibold leading-tight">Teen accounts</h1>
          <p className="text-sm text-white/80 mt-1 max-w-xs">Give your child (13–17) their own Jack Cash — you set the rules, approve payments and see everything.</p>
          <button data-testid="add-teen-btn" onClick={() => setShowAdd(true)}
            className="mt-4 inline-flex items-center gap-2 bg-white text-primary font-semibold text-sm px-4 h-11 rounded-full transition-transform duration-200 active:scale-95">
            <UserPlus size={17} /> Add a teen
          </button>
        </div>
      </div>
      {loading ? (
        <div className="py-16 grid place-items-center"><Spinner className="w-7 h-7 text-primary" /></div>
      ) : (
        <>
          {approvals.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center gap-2 px-1 mb-2 text-[11px] font-bold uppercase tracking-wider text-warning">
                <Clock size={13} /> Pending approvals
              </div>
              <div className="space-y-2">
                {approvals.map((a) => (
                  <div key={a.approval_id} data-testid={`approval-${a.approval_id}`} className="rounded-2xl bg-warning/5 border border-warning/30 p-4">
                    <div className="text-sm"><strong>{a.teen_name}</strong> wants to send <strong>{a.amount_display}</strong> to <span className="font-mono">{a.recipient_cash_tag}</span></div>
                    {a.note && <div className="text-xs text-muted-foreground mt-0.5">Note: {a.note}</div>}
                    <div className="flex gap-2 mt-3">
                      <button data-testid={`approve-${a.approval_id}`} disabled={acting === a.approval_id + "approve"} onClick={() => act(a.approval_id, "approve")}
                        className="flex-1 h-11 rounded-xl bg-success text-white font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60">
                        {acting === a.approval_id + "approve" ? <Spinner className="w-4 h-4" /> : <><Check size={16} /> Approve</>}
                      </button>
                      <button data-testid={`decline-${a.approval_id}`} disabled={acting === a.approval_id + "decline"} onClick={() => act(a.approval_id, "decline")}
                        className="flex-1 h-11 rounded-xl bg-destructive/10 text-destructive font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60">
                        {acting === a.approval_id + "decline" ? <Spinner className="w-4 h-4" /> : <><X size={16} /> Decline</>}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {teens.length === 0 ? (
            <EmptyState icon={Users} title="No teen accounts yet"
              body="Use “Add a teen” above to create a managed account for your child (13–17). You control sending, limits and approvals." />
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 px-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                <ShieldCheck size={13} /> Your teens
              </div>
              {teens.map((t) => <TeenCard key={t.teen_id} teen={t} onChanged={load} />)}
            </div>
          )}
        </>
      )}

      {showAdd && <AddTeenModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}
